import type { FormEvent } from 'react';
import { useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import type { TagInputValue } from '@hotjar/hotsauce';
import { useOrganizations, invite } from '@hotjar/organization';
import type { Invitation } from '@hotjar/organization';

import { useTranslation } from 'react-i18next';

import { Button, Field, TagInput, tokens } from '@hotjar/hotsauce';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useCurrentUser } from '@hotjar/user';
import { WideStep } from '@hotjar/webapp/features/Activation/SetupWizard/steps';
import type { TrackingSite } from '@hotjar/webapp/tracking/utils';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';

const Styled = {
  SkipButton: styled(Button)`
    margin-left: ${tokens.spacer.x2};
  `,
  ButtonWrapper: styled.div`
    margin-top: ${tokens.spacer.x3};
  `,
  Title: styled.h1`
    display: block;
    ${tokens.text('header4')};
    margin-bottom: ${tokens.spacer.x3};
  `,
  FollowingTitle: styled.h1`
    ${tokens.text('header4')};
    margin-bottom: ${tokens.spacer.x1};
  `,
  Subtitle: styled.p`
    ${tokens.text('body1')};
    color: ${tokens.color.text_light};
    margin-bottom: ${tokens.spacer.x2};
  `,
  Form: styled.form`
    max-width: 430px;
  `,
};

export interface InviteTeammateStepProps {
  onSkip: () => void;
  onSubmit: () => void;
}

const mapEmailsToInvitations = (emails: TagInputValue[]): Invitation[] => {
  return emails.map(({ name }) => ({
    email: name,
    access: 'write',
    include_tci: true,
  }));
};

export const InviteTeammateStep = ({
  onSkip,
  onSubmit,
}: InviteTeammateStepProps): React.ReactElement => {
  const { data: user } = useCurrentUser();
  const { data: currentSite } = useCurrentSite();
  const { data: organizations = [] } = useOrganizations(user?.id);
  const selectedOrg = organizations[0];
  const track = useTrack();
  const { t } = useTranslation();

  const [emails, setEmails] = useState<TagInputValue[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setEmails((prevEmails) => prevEmails.map((email) => ({ ...email, isLoading })));
  }, [isLoading]);

  const onSkipClicked = () => {
    track.click('skip', 'invite teammate registration step', {});
    onSkip();
  };

  const handleInvalid = (invalid: boolean) => {
    setIsInvalid(invalid);
  };

  const trackFormSubmitted = (success: boolean): void => {
    track.formSubmit(
      'invite teammate registration step',
      success,
      track.getGroupDataForSite(currentSite as TrackingSite),
      {},
    );
  };

  const getErrorMessage = (): string | undefined => {
    if (isInvalid) {
      return t('common:billing.invoices.billing-notifications.format-error');
    }
    if (error) {
      return error;
    }
    return undefined;
  };

  useEffect(() => {
    inputRef.current?.querySelector('input')?.focus();
  }, []);

  const areEmailsValid = (values: TagInputValue[]) =>
    values.every((value) => validateEmail(value.name));

  const handleChange = (mails: TagInputValue[]) => {
    const containsInvalidEmails = !areEmailsValid(mails);
    handleInvalid(containsInvalidEmails);
    setEmails(mails);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    invite(selectedOrg.accountId, selectedOrg.id, mapEmailsToInvitations(emails))
      .then((response) => {
        const apiError = response.error
          ? (response.error as string)
          : t('common:team-invite.invitation-error');
        if (apiError) {
          setError(apiError);
        }
        trackFormSubmitted(!response.error);
        onSubmit();
      })
      .catch(() => {
        setIsInvalid(true);
        trackFormSubmitted(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <WideStep>
      <Styled.Title>{t('activation:setup-wizard.invite-teammate.title-first-line')}</Styled.Title>
      <Styled.FollowingTitle>
        {t('activation:setup-wizard.invite-teammate.title-second-line-with-tci')}
      </Styled.FollowingTitle>
      <Styled.Subtitle>{t('activation:setup-wizard.invite-teammate.subtitle')}</Styled.Subtitle>
      <Styled.Form onSubmit={handleSubmit}>
        <Field
          labelText=""
          invalidText={getErrorMessage()}
          isInvalid={isInvalid}
          inputId="emails"
          size="full"
        >
          <TagInput
            ref={inputRef}
            value={emails}
            onChange={handleChange}
            placeholder={
              emails.length
                ? t('activation:setup-wizard.invite-teammate.emails-input-placeholder')
                : t('activation:setup-wizard.invite-teammate.emails-input-placeholder-empty-state')
            }
            id="emails"
            isInvalid={isInvalid}
            customTrigger=" "
            size="l"
            aria-labelledby="notifications-label"
          />
        </Field>
        <Styled.ButtonWrapper>
          <Button
            type="submit"
            isLoading={isLoading}
            isDisabled={isInvalid || !emails.length}
            size="l"
          >
            {t('common:team-invite.action-invite')}
          </Button>
          <Styled.SkipButton kind="secondary" size="l" onClick={onSkipClicked}>
            {t('activation:onboarding.organization-steps.skip-cta')}
          </Styled.SkipButton>
        </Styled.ButtonWrapper>
      </Styled.Form>
    </WideStep>
  );
};
